import { Table } from "antd";
import { QRCodeSVG } from 'qrcode.react';
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import { CommaSeparator, getDate } from "../../../utils";
import { useSelector } from "react-redux";
import { IMAGE_BASE_URL } from "../../../config/constants";

const ALL_COLUMNS = [
  { title: "Description", dataIndex: "product_description" },
  // { title: "HS Code", dataIndex: "hs_code" },
  { title: "Qty", dataIndex: "quantity" },
  { title: "UOM", dataIndex: "unit_of_measurement" },
  { title: "Retail Price", dataIndex: "fixed_notified_value_or_retail_price" },
  { title: "Sales Value", dataIndex: "value_sales_excluding_st" },
  { title: "Amount Excl. FED", dataIndex: "amount_excl_fed", optional: true, computed: true },
  { title: "FED", dataIndex: "fed_payable", optional: true },
  { title: "Tax %", dataIndex: "rate" },
  { title: "Sales Tax", dataIndex: "sales_tax_applicable" },
  // { title: "ST WHT", dataIndex: "sales_tax_with_held_at_source" },
  { title: "Extra Tax", dataIndex: "extra_tax", optional: true },
  // { title: "Further Tax", dataIndex: "further_tax", optional: true },
  { title: "Total", dataIndex: "total_values" },
  { title: "Discount", dataIndex: "discount", optional: true },
  // { title: "Adv. Tax", dataIndex: "advance_tax", optional: true },
  // { title: "SRO No", dataIndex: "sro_schedule_no", optional: true },
  // { title: "Sale Type", dataIndex: "sale_type" },
  // { title: "SRO Serial", dataIndex: "sro_item_serial_no", optional: true },
];

const FOOTER_NUMERIC = new Set([
  "fixed_notified_value_or_retail_price",
  "value_sales_excluding_st",
  "sales_tax_applicable",
  "sales_tax_with_held_at_source",
  "further_tax",
  "fed_payable",
  "discount",
  "advance_tax",
  "total_values",
]);

const BREAKABLE_COLS = new Set([
  "product_description", "unit_of_measurement",
  "sro_schedule_no", "sale_type", "sro_item_serial_no",
]);

const colHasValue = (items, field) =>
  items.some(item => {
    const v = item[field];
    return v !== null && v !== undefined && v !== "" && parseFloat(v) !== 0;
  });

function InvoiceHaroonFormat({ companyName, invoice, logo }) {

  const { userData } = useSelector((state) => ({ userData: state.user_detail }));

  const getVisibleColumns = (items = []) =>
    ALL_COLUMNS.filter(col => {
      if (!col.optional) return true;
      if (col.computed) return colHasValue(items, "fed_payable");
      return colHasValue(items, col.dataIndex);
    });

  const buildColumns = (items = []) => {
    const visible = getVisibleColumns(items);
    const widthPerCol = 950 / visible.length;

    return visible.map((col) => ({
      ...col,
      width: widthPerCol,
      render: (text, record) => {
        if (col.dataIndex === "hs_code") return text;

        if (col.dataIndex === "quantity") return Number(text).toFixed(2);

        if (col.dataIndex === "amount_excl_fed") {
          const val = parseFloat(record.value_sales_excluding_st || 0) - parseFloat(record.fed_payable || 0);
          return <p style={{ whiteSpace: "nowrap", textAlign: "right" }}>{CommaSeparator(val.toFixed(2))}</p>;
        }

        if (BREAKABLE_COLS.has(col.dataIndex)) {
          return <p style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{text}</p>;
        }

        return (
          <p style={{ whiteSpace: "nowrap", textAlign: "right" }}>
            {CommaSeparator(text)}
          </p>
        );
      },
    }));
  };

  const renderFooterSummary = (data = []) => {
    const visible = getVisibleColumns(data);

    const totals = {};
    FOOTER_NUMERIC.forEach(field => {
      totals[field] = data.reduce((sum, item) => {
        const val = parseFloat(item[field]);
        return sum + (isNaN(val) ? 0 : val);
      }, 0);
    });

    const totalQty = data.reduce((sum, item) => {
      const val = parseFloat(item.quantity);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

    // First 4 columns (Description, HS Code, Qty, UOM) are always present
    const restCols = visible.slice(4);

    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ background: "#f6f6f6", fontWeight: "bold" }}>
          <Table.Summary.Cell index={0}>Totals</Table.Summary.Cell>
          <Table.Summary.Cell index={1}>
            <p style={{ whiteSpace: "nowrap", textAlign: "right" }}>{totalQty.toFixed(2)}</p>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} />
          <Table.Summary.Cell index={3} />
          {restCols.map((col, i) => (
            <Table.Summary.Cell key={col.dataIndex + i} index={i + 4}>
              {col.dataIndex === "amount_excl_fed" ? (
                <p style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  {CommaSeparator((totals.value_sales_excluding_st - totals.fed_payable).toFixed(2))}
                </p>
              ) : FOOTER_NUMERIC.has(col.dataIndex) ? (
                <p style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  {CommaSeparator(totals[col.dataIndex])}
                </p>
              ) : null}
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <div className="card-body add-product pb-0 px-4">
      <div className="row justify-content-between align-items-center pb-4">
        <div className="col-lg-4 col-sm-4 col-4">
          <div className="d-flex align-items-center gap-2">
            <img
              src={`${IMAGE_BASE_URL}${logo}`}
              alt="img"
              className="mt-4 mb-3"
              style={{ height: "40px" }}
            />
            <h2 className="mt-4 mb-3">{companyName}</h2>
          </div>
        </div>
        <div className="col-lg-4 col-sm-4 col-4">
          <h2 className="mt-4 mb-3 text-center">Sales Tax Invoice</h2>
        </div>
        <div className="col-lg-4 col-sm-4 col-4">
          <div className="d-flex justify-content-end align-items-center gap-4">
            <ImageWithBasePath
              src="assets/img/fbr-logo.png"
              alt="img"
              width="110px"
            />
            {invoice?.fbr_invoice_number &&
              <QRCodeSVG
                value={invoice?.fbr_invoice_number}
                size={'96px'}
                level="M"
                minVersion={2.0}
              />
            }
          </div>
        </div>
      </div>
      <div className="emp-details total-order max-widthauto py-3s ms-0 my-2 mt-4" style={{
        border: "1px solid #ddd",
        fontSize: "14px",
      }}>
        <div className="row">
          <div className="col-lg-3 col-sm-3 col-3">
            <strong>Invoice Type:</strong> {invoice?.invoice_type}
          </div>
          <div className="col-lg-3 col-sm-3 col-3">
            <strong>Invoice Ref No:</strong> {invoice?.invoice_ref_no}
          </div>
          <div className="col-lg-2 col-sm-2 col-2">
            <strong>Date:</strong> {getDate(invoice?.invoice_date)}
          </div>
          <div className="col-lg-4 col-sm-4 col-4">
            <strong>FBR #:</strong> {invoice?.fbr_invoice_number || "-"}
          </div>
        </div>
      </div>
      <div className="emp-details py-3" style={{ border: "1px solid #e9ecef" }}>
        <div className="row justify-content-between mb-2">
          <div className="col-lg-5 col-sm-5 col-12">
            <div className="emp-name-id mb-3">
              <h5>Seller Information</h5>
            </div>
            <div className="emp-name-id mb-3">
              <div className="row">
                <div className="col-lg-3 col-sm-3 col-3">
                  <strong>Name:</strong>
                </div>
                <div className="col-lg-9 col-sm-9 col-9">
                  <p>
                    {invoice?.seller_business_name}
                  </p>
                </div>
              </div>
            </div>
            <div className="emp-name-id mb-3">
              <div className="row">
                <div className="col-lg-3 col-sm-3 col-3">
                  <strong>Address:</strong>
                </div>
                <div className="col-lg-9 col-sm-9 col-9">
                  <p>
                    {invoice?.seller_address} {invoice?.seller_province}
                  </p>
                </div>
              </div>
            </div>
            <div className="emp-name-id mb-3">
              <div className="row">
                <div className="col-lg-3 col-sm-3 col-3">
                  <strong>NTN/CNIC:</strong>
                </div>
                <div className="col-lg-9 col-sm-9 col-9">
                  <p>
                    {invoice?.seller_ntn_cnic}
                  </p>
                </div>
              </div>
            </div>
            <div className="emp-name-id">
              <div className="row">
                <div className="col-lg-3 col-sm-3 col-3">
                  <strong>STRN:</strong>
                </div>
                <div className="col-lg-9 col-sm-9 col-9">
                  <p>
                    {invoice?.seller_strn || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-5 col-sm-5 col-12">
            <div className="emp-name-id mb-3">
              <h5>Buyer Information</h5>
            </div>
            <div className="emp-name-id mb-3">
              <div className="row">
                <div className="col-lg-3 col-sm-3 col-3">
                  <strong>Name:</strong>
                </div>
                <div className="col-lg-9 col-sm-9 col-9">
                  <p>
                    {invoice?.buyer_business_name}
                  </p>
                </div>
              </div>
            </div>
            <div className="emp-name-id mb-3">
              <div className="row">
                <div className="col-lg-3 col-sm-3 col-3">
                  <strong>Address:</strong>
                </div>
                <div className="col-lg-9 col-sm-9 col-9">
                  <p>
                    {invoice?.buyer_address} {invoice?.buyer_province}
                  </p>
                </div>
              </div>
            </div>
            <div className="emp-name-id mb-3">
              <div className="row">
                <div className="col-lg-3 col-sm-3 col-3">
                  <strong>NTN/CNIC:</strong>
                </div>
                <div className="col-lg-9 col-sm-9 col-9">
                  <p>
                    {invoice?.buyer_ntn_cnic || '-'}
                  </p>
                </div>
              </div>
            </div>
            <div className="emp-name-id">
              <div className="row">
                <div className="col-lg-3 col-sm-3 col-3">
                  <strong>STRN:</strong>
                </div>
                <div className="col-lg-9 col-sm-9 col-9">
                  <p>
                    {invoice?.buyer_strn || '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .haroon-invoice .ant-table,
        .haroon-invoice .ant-table-thead > tr > th,
        .haroon-invoice .ant-table-tbody > tr > td,
        .haroon-invoice .ant-table-summary > tr > td { font-size: 14px !important; }
      `}</style>
      {invoice?.invoice_items.length > 0 &&
        <div className="mb-2 table-responsive haroon-invoice">
          <Table
            className="table datanew dataTable no-footer custom-table"
            columns={buildColumns(invoice?.invoice_items)}
            dataSource={invoice?.invoice_items}
            pagination={false}
            bordered
            rowClassName={(_, index) => (index === invoice?.invoice_items.length - 1 ? "last-row" : "")}
            summary={() => renderFooterSummary(invoice?.invoice_items)}
          />
        </div>
      }
      {invoice?.invoice_items.length > 0 && (() => {
        const items = invoice.invoice_items;
        const sum = (field) => items.reduce((acc, item) => {
          const v = parseFloat(item[field]);
          return acc + (isNaN(v) ? 0 : v);
        }, 0);
        const totalValues = sum("total_values");
        const furtherTax = sum("further_tax");
        const advanceTax = sum("advance_tax");
        const grandTotal = totalValues + furtherTax + advanceTax;
        const showFurther = colHasValue(items, "further_tax");
        const showAdvance = colHasValue(items, "advance_tax");

        const summaryRows = [
          ...(showFurther ? [{ key: "further", label: "Further Tax", value: furtherTax }] : []),
          ...(showAdvance ? [{ key: "advance", label: "Advance Tax U/S 236", value: advanceTax }] : []),
          { key: "grand", label: "Grand Total", value: grandTotal, isTotal: true },
        ];

        const summaryColumns = [
          {
            title: "Description",
            dataIndex: "label",
            onCell: (row) => row.isTotal ? { style: { background: "#f6f6f6", fontWeight: 700 } } : {},
            render: (text) => text,
          },
          {
            title: "Amount",
            dataIndex: "value",
            align: "right",
            onCell: (row) => row.isTotal ? { style: { background: "#f6f6f6", fontWeight: 700 } } : {},
            render: (val) => CommaSeparator(val.toFixed(2)),
          },
        ];

        return (
          <div className="d-flex justify-content-end mb-3 mt-1">
            <div style={{ width: 330 }}>
              <Table
                className="table datanew dataTable no-footer custom-table"
                columns={summaryColumns}
                dataSource={summaryRows}
                pagination={false}
                bordered
                showHeader={false}
              />
            </div>
          </div>
        );
      })()}
      <div style={{ height: '50px' }}></div>
    </div>
  );
}

export default InvoiceHaroonFormat;
